import React from 'react';

import { bool, number } from 'prop-types';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroller';

import CircularProgress from '@mui/material/CircularProgress';

import EmojiEmptyState from 'components/Common/EmojiEmptyState';
import Flexbox from 'components/Common/Flexbox';
import Grid from 'components/Common/Grid';

import Api from 'core/api';
import useModal from 'hooks/useModal';
import usePaginatedApi from 'hooks/usePaginatedApi';
import { isNonEmptyArray } from 'utils/arrays';

import CredentialCard from 'modules/CredentialWallet/components/CredentialCard';

import s from './CredentialsGrid.css';

const CredentialsGrid = ({ studentId, isPublic }) => {
  const { t } = useTranslation();
  const { showModal, hideModal } = useModal();
  const {
    items: credentials,
    isLoading,
    loadNextPage,
    hasNextPage,
  } = usePaginatedApi({
    apiFunc: Api.clr.getAllStudentCredentials,
    apiFuncName: 'clr.getAllStudentCredentials',
    filters: { student: studentId },
    useLimitOffset: true,
    defaultOrderBy: 'credential_awarded',
    defaultOrder: 'desc',
  });

  return isNonEmptyArray(credentials) ? (
    <Flexbox flexDirection="column">
      <InfiniteScroll
        pageStart={0}
        loader={
          <Flexbox justifyContent="center" alignItems="center">
            <CircularProgress />
          </Flexbox>
        }
        loadMore={!isLoading && loadNextPage}
        hasMore={hasNextPage}
        threshold={10}
        useWindow={false}
      >
        <Grid container width="100%" columnSpacing={3} rowSpacing={3}>
          {credentials.map(credential => (
            <Grid item desktop={4} mobile={12} key={credential.id}>
              <CredentialCard
                credential={credential}
                studentId={studentId}
                isPublic={isPublic}
              />
            </Grid>
          ))}
        </Grid>
      </InfiniteScroll>
      <Flexbox className={s.spacer} />
    </Flexbox>
  ) : (
    <Flexbox flexDirection="column" alignItems="center" mt={3}>
      <EmojiEmptyState
        className={s.emptyState}
        emojiProps={{
          emoji: '🏜',
          label: t('CredentialWallet.CredentialsGrid.DesertEmoji'),
        }}
        mainText={t('CredentialWallet.CredentialsGrid.NoCredentialsYet')}
        secondaryText={t(
          'CredentialWallet.CredentialsGrid.CredentialsIssuedOrEarnedI',
        )}
        buttonProps={
          !isPublic && {
            label: t('CredentialWallet.CredentialsGrid.AddCredentials'),
            variant: 'filled',
            onClick: () => {
              showModal('ADD_CREDENTIAL_MODAL', {
                onClose: hideModal,
              });
            },
          }
        }
      />

      <Flexbox className={s.spacer} />
    </Flexbox>
  );
};

CredentialsGrid.propTypes = {
  studentId: number,
  isPublic: bool,
};

CredentialsGrid.defaultProps = {
  studentId: undefined,
  isPublic: false,
};

export default CredentialsGrid;
